import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';
import { 
  ArrowRight, 
  CheckCircle, 
  MapPin, 
  Search, 
  Share2, 
  TrendingUp,
  Star,
  Phone,
  Mail,
  Building2,
  Users,
  Award,
  ChevronRight
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { locationServices, allLocationPages } from '../data/locationServices';

const iconMap = {
  MapPin,
  Search,
  Share2,
  TrendingUp
};

export const LocationServicePage = ({ onOpenAuditModal, pageSlug }) => {
  const navigate = useNavigate();

  // Use prop directly instead of useParams
  const slug = pageSlug;

  // Parse slug to get service and location
  const pageInfo = allLocationPages.find(p => p.slug === slug);
  
  usePageMeta(pageInfo ? {
    title: pageInfo.pageTitle,
    description: `Professional ${pageInfo.service} services in ${pageInfo.location}. Expert digital marketing solutions tailored for local businesses.`
  } : {});
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!pageInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="location-page-not-found">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1A1A1A] mb-4">Page Not Found</h1>
          <p className="text-[#525252] mb-6">The service page you're looking for doesn't exist.</p>
          <Button asChild className="bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] rounded-full">
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  const pageData = locationServices.getPageData(pageInfo.service, pageInfo.location);
  const { service, location } = pageData;
  const ServiceIcon = iconMap[service.icon] || Search;

  // Get related pages (same service, different locations)
  const relatedPages = allLocationPages.filter(
    p => p.service === pageInfo.service && p.location !== pageInfo.location
  ).slice(0, 4);

  // Get other services in same location
  const otherServices = allLocationPages.filter(
    p => p.location === pageInfo.location && p.service !== pageInfo.service
  );

  return (
    <main data-testid={`location-page-${slug}`}>
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-[#1A1A1A] to-[#2E2E2E] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#FFAA17] rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#2F80ED] rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-[#FFAA17]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/services/seo-organic-growth" className="hover:text-[#FFAA17]">Services</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{pageData.pageTitle}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FFAA17]/20 text-[#FFAA17] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <MapPin className="w-4 h-4" />
                {location.name}, {location.country}
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {pageData.h1}
              </h1>
              
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                {pageData.heroSubtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onOpenAuditModal}
                  className="bg-[#FFAA17] hover:bg-[#E69500] text-[#1A1A1A] font-semibold rounded-full px-8 py-4 text-lg shadow-lg"
                  data-testid="hero-cta"
                >
                  {pageData.ctaText}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 text-lg"
                >
                  <Link to="/contact">Talk to Expert</Link>
                </Button>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-[#FFAA17] rounded-xl flex items-center justify-center">
                    <ServiceIcon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{service.name}</h3>
                    <p className="text-gray-400">in {location.name}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {service.benefits.slice(0, 4).map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-white">
                      <CheckCircle className="w-5 h-5 text-[#FFAA17] flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <Award className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#1A1A1A]">6+</p>
              <p className="text-sm text-gray-500">Years Experience</p>
            </div>
            <div className="text-center">
              <Users className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#1A1A1A]">150+</p>
              <p className="text-sm text-gray-500">Happy Clients</p>
            </div>
            <div className="text-center">
              <TrendingUp className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#1A1A1A]">4000%</p>
              <p className="text-sm text-gray-500">Avg. ROI Growth</p>
            </div>
            <div className="text-center">
              <Building2 className="w-8 h-8 text-[#FFAA17] mx-auto mb-2" />
              <p className="text-3xl font-bold text-[#1A1A1A]">{location.stats.businesses}</p>
              <p className="text-sm text-gray-500">Businesses in {location.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About the Service */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
                Why Choose Us
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-6">
                Expert {service.shortName} for {location.name} Businesses
              </h2>
              <p className="text-[#525252] mb-6 leading-relaxed">
                {location.marketInsight} Our team of {service.shortName.toLowerCase()} experts understands the unique challenges and opportunities in the {location.name} market.
              </p>
              <p className="text-[#525252] mb-8 leading-relaxed">
                {service.description} Whether you're a startup or established business in {location.businessAreas.slice(0, 3).join(', ')}, or other areas of {location.name}, we deliver results that matter.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {service.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#27AE60] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#525252]">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#F9FAFB] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6">
                {location.name} Market Overview
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-[#525252]">Active Businesses</span>
                  <span className="font-semibold text-[#1A1A1A]">{location.stats.businesses}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-[#525252]">Internet Users</span>
                  <span className="font-semibold text-[#1A1A1A]">{location.stats.internetUsers}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-[#525252]">Search Volume</span>
                  <span className="font-semibold text-[#FFAA17]">{location.stats.searchVolume}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-[#525252]">Key Business Areas</span>
                  <span className="font-semibold text-[#1A1A1A] text-right text-sm">{location.businessAreas.slice(0, 3).join(', ')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-24 bg-[#F2F2F2]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              Our Process
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
              How We Deliver {service.shortName} Results in {location.name}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.process.map((step, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 relative">
                <div className="w-12 h-12 bg-[#FFAA17] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                  {idx + 1}
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">{step.title}</h3>
                <p className="text-[#525252] text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
              FAQs
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
              {service.shortName} Questions for {location.name} Businesses
            </h2>
          </div>

          <Accordion type="single" collapsible>
            {service.faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`faq-${idx}`}
                className="bg-[#F9FAFB] rounded-xl mb-3 border border-[#E5E5E5] px-6"
              >
                <AccordionTrigger className="text-left font-semibold text-[#1A1A1A] hover:text-[#FFAA17] py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#525252] pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Other Locations */}
      {relatedPages.length > 0 && (
        <section className="py-16 md:py-24 bg-[#F9FAFB]">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
                We Also Serve
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
                {service.shortName} in Other Locations
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedPages.map((page, idx) => {
                const loc = locationServices.locations[page.location];
                return (
                  <Link
                    key={idx}
                    to={`/services/${page.slug}`}
                    className="bg-white rounded-xl p-6 hover:shadow-lg transition-all group"
                  >
                    <MapPin className="w-8 h-8 text-[#FFAA17] mb-3" />
                    <h3 className="font-semibold text-[#1A1A1A] group-hover:text-[#FFAA17] transition-colors">
                      {service.shortName} in {loc.name}
                    </h3>
                    <p className="text-sm text-[#525252] mt-1">{loc.country}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Other Services in Location */}
      {otherServices.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold tracking-wide uppercase text-[#FFAA17] mb-3">
                More Services
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
                Other Digital Marketing Services in {location.name}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {otherServices.map((page, idx) => {
                const svc = locationServices.services[page.service];
                const SvcIcon = iconMap[svc.icon] || Search;
                return (
                  <Link
                    key={idx}
                    to={`/services/${page.slug}`}
                    className="bg-[#F9FAFB] rounded-xl p-6 hover:shadow-lg transition-all group"
                  >
                    <div className="w-12 h-12 bg-[#FFAA17]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FFAA17] transition-colors">
                      <SvcIcon className="w-6 h-6 text-[#FFAA17] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-[#1A1A1A] group-hover:text-[#FFAA17] transition-colors">
                      {svc.shortName} in {location.name}
                    </h3>
                    <p className="text-sm text-[#525252] mt-2 line-clamp-2">{svc.description}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#FFAA17]">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4">
            Ready to Grow Your {location.name} Business?
          </h2>
          <p className="text-[#1A1A1A]/80 mb-8 text-lg">
            Get a free {service.shortName.toLowerCase()} audit and discover how we can help you dominate the {location.name} market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={onOpenAuditModal}
              className="bg-[#1A1A1A] hover:bg-[#2E2E2E] text-white rounded-full px-8 py-4 text-lg"
            >
              Get Free Audit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A]/10 rounded-full px-8 py-4 text-lg"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": pageData.fullTitle,
            "description": pageData.metaDescription,
            "provider": {
              "@type": "Organization",
              "name": "Digital Startup",
              "url": "https://digitalstartup.in"
            },
            "areaServed": {
              "@type": "Place",
              "name": location.name
            },
            "serviceType": service.name
          })
        }}
      />
    </main>
  );
};

export default LocationServicePage;
